import type { DetailsData, Product, ProductCardExtended, ProductSetItem } from '#root/api/clients/product/data-contracts'
import type { CartItem } from '#types/cart'
import type { P13nImpressionContext, ProductContext, ShopTheLookContext } from '#types/gtm'
import type { BloomreachRecommendedProduct } from '#types/p13n'
import type { ProductDetails } from '#types/product'

const mapFilters = (filters) => {
  if (filters.length)
    return Object.entries(JSON.parse(filters)).map(([category, values]) => ({ category, values }))
  return []
}

const getBaseEvent = (eventLabel?: string | undefined, nonInteractive = true) => ({
  eventCategory: 'Enhanced Ecommerce',
  eventLabel,
  customMetrics: {},
  customVariables: {},
  nonInteractive,
  _clear: true
})

export default {
  // https://digital.vfc.com/wiki/display/ECOM15/a.+productImpressions
  onProductImpression: (
    products: CartItem[] | ProductCardExtended[],
    filters: string,
    context: ProductContext
  ) => ({
    ...getBaseEvent(),
    event: 'productImpressions',
    eventAction: 'Product Impressions',
    ecommerce: {
      currencyCode: products[0]?.currency,
      impressions: getProductObjectList(products, context)
    },
    customVariables: {
      list_type: 'grid',
      productLayout: `${context.layout} column`,
      sortOption: context.sortOption,
      filters: mapFilters(filters)
    }
  }),
  //
  onProductSetImpression: (products: ProductSetItem[], context: ShopTheLookContext) => ({
    ...getBaseEvent(),
    event: 'productImpressions',
    eventAction: 'Product Impressions',
    ecommerce: {
      currencyCode: context.currency,
      impressions: getProductSetItemObjectList(products, context)
    },
    customVariables: {
      list_type: 'Shop the Look'
    }
  }),
  onProductGridImpression: (products: ProductCardExtended[], context: any) => ({
    ...getBaseEvent(),
    event: 'productImpressions',
    eventAction: 'Product Impressions',
    ecommerce: {
      currencyCode: context.currency,
      impressions: getProductObjectList(products, context)
    }
  }),
  // https://digital.vfc.com/wiki/display/ECOM15/a.+productImpressions#:~:text=dataLayer%20event%3A%20productRecImpressions
  onProductRecImpressions: (
    products: BloomreachRecommendedProduct[],
    { currency, placement, title }: P13nImpressionContext
  ) => ({
    event: 'productImpressions',
    eventCategory: 'Enhanced Ecommerce',
    eventAction: 'Product Impressions',
    nonInteractive: true,
    ecommerce: {
      currencyCode: currency,
      impressions: products.map((product, i) =>
        getProductRecommendationObject(product, i + 1, { title, placement }))
    },
    customMetrics: {},
    customVariables: {
      list_type: 'recommendation carousel',
      recommendationId: products[0]?.recommendationId,
      recommendationName: products[0]?.recommendationName,
      recommendationVariant: products[0]?.recommendationVariant
    },
    _clear: true
  }),
  // https://digital.vfc.com/wiki/display/ECOM15/b.+productClick
  onProductClick: (product: CartItem | Product | ProductDetails, context: ProductContext) => ({
    ...getBaseEvent(product.id, false),
    event: 'productClick',
    eventAction: 'Product Clicks',
    ecommerce: {
      currencyCode: product.currency,
      click: {
        actionField: {
          action: context.action,
          affiliation: `${useAppConfig().api.brandPrefix}-${useCountryCode()}`,
          list: context.list || getListDescription({
            breadcrumbs: getPageBreadcrumb(context.breadcrumbs)?.join(' - '),
            searchTerm: context.searchTerm
          }),
        },
        products: getProductObjectList([product] as CartItem[] | DetailsData[], context)
      }
    }
  }),
  //
  onProductSetClick: (product: ProductSetItem, context: ShopTheLookContext) => ({
    ...getBaseEvent(product.id, false),
    event: 'productClick',
    eventAction: 'Product Clicks',
    ecommerce: {
      currencyCode: context.currency,
      click: {
        actionField: {
          action: 'Click Shop the Look Item',
          affiliation: `${useAppConfig().api.brandPrefix}-${useCountryCode()}`,
          list: 'Shop the Look',
        },
        products: getProductSetItemObjectList([product], context)
      }
    }
  }),
  // https://digital.vfc.com/wiki/display/ECOM15/b.+productClick#:~:text=dataLayer%20event%3A%20productRecClick
  onProductRecClick: (
    product: BloomreachRecommendedProduct,
    position: number,
    {
      action,
      currency,
      placement,
      title,
      vendor
    }: P13nImpressionContext
  ) => {
    const products = [getProductRecommendationObject(product, position, { placement, title })]
    const list = getRecommendationListDescription({ placement, title })

    setInteractionOrigin('recommendation-carousel', true)
    setGtmCartProductsMap(product.id, { ...product, list })

    return {
      ...getBaseEvent(product.id, false),
      event: 'productClick',
      eventAction: 'Product Clicks',
      ecommerce: {
        currencyCode: currency,
        click: {
          actionField: {
            action,
            list,
            category: list
          },
          products
        }
      },
      customVariables: {
        list_type: 'recommendation carousel',
        placement,
        vendor,
        recommendationId: product.recommendationId,
        recommendationName: product.recommendationName,
        recommendationVariant: product.recommendationVariant
      }
    }
  },
  // https://digital.vfc.com/wiki/display/ECOM15/c.+productDetailView
  onProductDetailView: (product: any, context: ProductContext) => ({
    ...getBaseEvent(product.id),
    event: 'productDetailView',
    eventAction: 'Product Detail View',
    ecommerce: {
      currencyCode: product.currency,
      detail: {
        actionField: {
          viewType: context.viewType,
          list: context.list || getListDescription({
            breadcrumbs: getPageBreadcrumb(context.breadcrumbs)?.join(' - '),
            searchTerm: context.searchTerm,
            appSource: context.appSource,
            productId: product.id
          })
        },
        products: getProductObjectList([product] as CartItem[] | DetailsData[], {
          omitPosition: true,
          ...context
        })
      }
    }
  }),
  // https://digital.vfc.com/wiki/display/ECOM15/d.+addToCart+and+removeFromCart
  onRemoveFromCart: () => {
    // To be implemented
  },
  // https://digital.vfc.com/wiki/display/ECOM15/e.+checkout
  onCheckout: () => {
    // To be implemented
  },
  // https://digital.vfc.com/wiki/display/ECOM15/f.+checkoutOption
  onCheckoutOption: () => {
    // To be implemented
  },
  // https://digital.vfc.com/wiki/display/ECOM15/g.+checkoutCompletion
  onCheckouCompletion: () => {
    // To be implemented
  },
  // https://digital.vfc.com/wiki/display/ECOM15/h.+cartUpdate
  onCartUpdate: () => {
    // To be implemented
  },
}
